import { Box, Button, Flex, Stack, Text } from '@chakra-ui/react'
import {
  faBellOn,
  faHourglassClock,
  faWarning,
  type IconDefinition,
} from '@fortawesome/pro-solid-svg-icons'
import { useState } from 'react'

import type { PostDetails } from '@/components/Community/Post/types'
import Icon from '@/components/UI/Icon/Icon'
import Divider from '@/components/Worlds/UI/Divider'

import CancelLiveHelpRequestModal from './CancelLiveHelpRequestModal'

const REMINDERS: { icon: IconDefinition; text: string }[] = [
  {
    icon: faWarning,
    text: `Keep this page open to keep your help request active! If you leave this page, you'll have to request help again.`,
  },
  {
    icon: faBellOn,
    text: `Turn on your sound! We'll play a tone when a tutor opens a room for you.`,
  },
  {
    icon: faHourglassClock,
    text: `Be ready to join your tutor's Zoom within 2 minutes!`,
  },
]

export default function AuthorAwaitingLiveHelpBanner({
  post,
  postIsFullView,
}: {
  post: PostDetails
  postIsFullView: boolean
}): JSX.Element {
  const [showCancelRequestModal, setShowCancelRequestModal] = useState(false)

  return (
    <>
      <CancelLiveHelpRequestModal
        postId={post.id}
        open={showCancelRequestModal}
        onClose={() => setShowCancelRequestModal(false)}
      />
      <Stack
        direction="column"
        width="100%"
        p="16px"
        bgColor="bg.info"
        borderRadius="8px"
        spacing="16px"
      >
        <Stack
          direction={postIsFullView ? { base: 'column', md: 'row' } : 'column'}
          align={postIsFullView ? { base: 'left', md: 'center' } : 'left'}
          justify="space-between"
        >
          <Stack spacing={0}>
            <Text fontWeight="bold">You&apos;re requesting help over Zoom!</Text>
            <Text>If a tutor is available, we&apos;ll let you know as soon as possible.</Text>
          </Stack>
          <Button
            variant="danger-outline"
            flexShrink={0}
            onClick={() => setShowCancelRequestModal(true)}
          >
            I don&apos;t need help anymore
          </Button>
        </Stack>

        <Divider />

        <Stack direction="column" spacing="16px">
          <Text variant="eyebrow" color="fg.muted">
            Reminders
          </Text>
          {REMINDERS.map(({ icon, text }) => (
            <Flex key={text}>
              <Box minW="32px">
                <Icon faIcon={icon} color="fg.subtle" size="md" />
              </Box>
              <Text>{text}</Text>
            </Flex>
          ))}
        </Stack>
      </Stack>
    </>
  )
}
